<template>
  <div>
    <b-modal
      ref="product-stock-info"
      :title="modalTitle"
      @ok="handleInfoInput"
      @close="hideModal"
      @hide="hideModal"
      ok-title="Lưu"
      cancel-title="Hủy"
      :ok-disabled="isSubmitDisabled"
      size="md"
    >
      <!-- Chassis Engine Product Type -->
      <template v-if="isChassisEngineProduct">
        <b-form-group
          id="chassis-input-group"
          label="Số khung"
          label-for="chassis-input"
          :invalid-feedback="errors.chassis"
          :state="getValidationState('chassis')"
        >
          <b-form-input
            id="chassis-input"
            v-model="formData.chassisNumber"
            type="text"
            required
            placeholder="Nhập số khung"
            :state="getValidationState('chassis')"
          />
        </b-form-group>

        <!-- Engine Number Toggle -->
        <b-form-group>
          <b-form-checkbox
            id="engine-toggle"
            v-model="includeEngineNumber"
            :disabled="!isImportMode"
            switch
            size="sm"
          >
            <strong>Nhập số máy</strong>
          </b-form-checkbox>
          <small class="text-muted">
            <span v-if="autoDetectedEngine && isExportMode">
              Đã tự động phát hiện và điền số máy từ số khung
            </span>
          </small>
        </b-form-group>

        <b-form-group
          id="engine-input-group"
          label="Số máy"
          label-for="engine-input"
          :invalid-feedback="errors.engine"
          :state="getValidationState('engine')"
        >
          <b-form-input
            id="engine-input"
            v-model="displayEngineNumber"
            type="text"
            placeholder="Nhập số máy"
            :state="getValidationState('engine')"
            :readonly="isEngineReadonly"
            :disabled="!includeEngineNumber"
          />
          <small
            v-if="isEngineReadonly && includeEngineNumber"
            class="text-muted"
          >
            Số máy được tự động điền từ số khung
          </small>
          <small v-else-if="!includeEngineNumber" class="text-muted">
            Số máy sẽ được đặt thành {{ EMPTY_ENGINE_NUMBER }} khi không nhập
          </small>
        </b-form-group>
      </template>

      <!-- Other Product Types -->
      <template v-else>
        <b-form-group
          id="serial-input-group"
          label="Số seri"
          label-for="serial-input"
          :invalid-feedback="errors.serial"
          :state="getValidationState('serial')"
        >
          <b-form-input
            id="serial-input"
            v-model="formData.serialNumber"
            type="text"
            required
            placeholder="Nhập số seri"
            :state="getValidationState('serial')"
          />
        </b-form-group>
      </template>

      <!-- Loading indicator -->
      <div v-if="isLoading" class="text-center py-2">
        <b-spinner small />
      </div>
    </b-modal>
  </div>
</template>

<script>
import { PRODUCT_TYPE_ENUM, EMPTY_ENGINE_NUMBER } from '@/utils/enum';
import { TIME_TRIGGER } from '@/utils/constants';
import { debounce } from 'lodash';
import productService from '../../../core/services/product';

const MODES = {
  CREATE: 'create',
  IMPORT: 'import',
  EXPORT: 'export',
};

const VALIDATION_FIELDS = ['chassis', 'engine', 'serial'];

export default {
  name: 'ProductStockInfoModal',

  props: {
    productItem: {
      type: Object,
      required: false,
      default: () => ({}),
      validator: (value) => !value || typeof value === 'object',
    },
    mode: {
      type: String,
      default: MODES.CREATE,
      validator: (value) => Object.values(MODES).includes(value),
    },
    validEngineFromChassis: {
      type: Boolean,
      default: false,
    },
    sourceStoreId: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      PRODUCT_TYPE_ENUM,
      EMPTY_ENGINE_NUMBER,
      MODES,
      formData: this.getInitialFormData(),
      includeEngineNumber: false,
      autoDetectedEngine: false,
      index: null,
      validEngineNumberFromChassis: '',
      isLoading: false,
      errors: this.getInitialErrors(),
      debouncedFetchEngineNumber: null,
    };
  },

  computed: {
    modalTitle() {
      const productName = this.productItem?.name || 'sản phẩm';
      const modeText = {
        [MODES.IMPORT]: 'nhập kho',
        [MODES.EXPORT]: 'xuất kho',
        [MODES.CREATE]: 'tạo',
      }[this.mode];
      return `Nhập thông tin ${modeText} cho sản phẩm ${productName}`;
    },

    isChassisEngineProduct() {
      return this.productItem?.productType === PRODUCT_TYPE_ENUM.CHASSIS_ENGINE;
    },

    isImportMode() {
      return this.mode === MODES.IMPORT;
    },

    isExportMode() {
      return this.mode === MODES.EXPORT;
    },

    shouldFetchEngineNumber() {
      return !this.isImportMode;
    },

    isEngineReadonly() {
      return this.includeEngineNumber && this.shouldFetchEngineNumber;
    },

    isChassisLengthValid() {
      return this.formData.chassisNumber?.length >= 6;
    },

    displayEngineNumber: {
      get() {
        return this.includeEngineNumber
          ? this.formData.engineNumber
          : EMPTY_ENGINE_NUMBER;
      },
      set(value) {
        if (this.includeEngineNumber) {
          this.formData.engineNumber = value;
        }
      },
    },

    actualEngineNumber() {
      return this.includeEngineNumber
        ? this.formData.engineNumber
        : EMPTY_ENGINE_NUMBER;
    },

    isSubmitDisabled() {
      return this.isLoading || this.hasValidationErrors || !this.isFormValid;
    },

    hasValidationErrors() {
      return Object.values(this.errors).some((error) => error !== '');
    },

    isFormValid() {
      if (this.isChassisEngineProduct) {
        return (
          this.formData.chassisNumber.trim() &&
          (!this.includeEngineNumber || this.formData.engineNumber.trim())
        );
      }
      return this.formData.serialNumber.trim();
    },
  },

  watch: {
    'formData.chassisNumber': {
      handler() {
        this.clearError('chassis');
        this.formData.engineNumber = '';

        if (this.isChassisLengthValid && this.shouldFetchEngineNumber) {
          this.debouncedFetchEngineNumber();
        } else if (this.shouldFetchEngineNumber) {
          this.resetEngineData();
        }
      },
      immediate: false,
    },

    'formData.engineNumber': {
      handler() {
        this.clearError('engine');
        if (this.shouldFetchEngineNumber && this.includeEngineNumber) {
          this.validateEngineNumber();
        }
      },
      immediate: false,
    },

    'formData.serialNumber': {
      handler() {
        this.clearError('serial');
      },
      immediate: false,
    },

    includeEngineNumber: {
      handler(newValue) {
        this.clearErrors(['engine', 'chassis']);
        if (!newValue) {
          this.resetEngineData();
        } else if (this.isChassisLengthValid && this.shouldFetchEngineNumber) {
          this.debouncedFetchEngineNumber();
        }
      },
      immediate: false,
    },
  },

  created() {
    this.initializeFormData();
    this.initializeDebouncedFunctions();
    this.initializeData();
  },

  beforeDestroy() {
    this.cancelDebouncedFunctions();
  },

  methods: {
    // Initialization methods
    getInitialFormData() {
      return {
        chassisNumber: '',
        engineNumber: '',
        serialNumber: '',
      };
    },

    getInitialErrors() {
      return VALIDATION_FIELDS.reduce((acc, field) => {
        acc[field] = '';
        return acc;
      }, {});
    },

    initializeDebouncedFunctions() {
      this.debouncedFetchEngineNumber = debounce(
        this.fetchEngineNumberFromChassis,
        TIME_TRIGGER,
      );
    },

    initializeFormData() {
      if (!this.productItem) return;

      this.formData = {
        chassisNumber: this.productItem.chassisNumber || '',
        engineNumber: this.productItem.engineNumber || '',
        serialNumber: this.productItem.serialNumber || '',
      };
    },

    initializeData() {
      if (!this.isImportMode) {
        this.includeEngineNumber = true;
        this.autoDetectedEngine = true;
      }
    },

    hasValidEngineNumber(engineNumber) {
      if (this.isImportMode) {
        return engineNumber === EMPTY_ENGINE_NUMBER || engineNumber?.length > 3;
      }
      return !!(engineNumber && engineNumber !== EMPTY_ENGINE_NUMBER);
    },

    // Modal control methods
    showModal(productItem, index = null) {
      this.assignData(productItem, index);
      this.$refs['product-stock-info'].show();
    },

    hideModal() {
      this.resetData();
      this.$refs['product-stock-info'].hide();
    },

    // Form handling methods
    async handleInfoInput() {
      if (!this.validateForm()) return;

      const data = {
        proId: this.productItem.proId,
        index: this.index,
        chassisNumber: this.formData.chassisNumber,
        engineNumber: this.actualEngineNumber,
        serialNumber: this.formData.serialNumber,
      };

      this.$emit('updateProductStockInfo', data);
    },

    assignData(productItem, index) {
      if (!productItem) return;

      this.formData = {
        chassisNumber: productItem.chassisNumber || '',
        engineNumber: productItem.engineNumber || '',
        serialNumber: productItem.serialNumber || '',
      };
      this.index = index;
      this.autoDetectedEngine = false;

      if (this.isImportMode) {
        this.includeEngineNumber = this.hasValidEngineNumber(
          productItem.engineNumber,
        );
      } else {
        this.includeEngineNumber = true;
      }
    },

    resetData() {
      this.formData = this.getInitialFormData();
      this.index = null;
      this.includeEngineNumber = false;
      this.autoDetectedEngine = false;
      this.validEngineNumberFromChassis = '';
      this.clearAllErrors();
      this.isLoading = false;
    },

    resetEngineData() {
      this.formData.engineNumber = '';
      this.validEngineNumberFromChassis = '';
      this.autoDetectedEngine = false;
    },

    // Engine number fetching
    async fetchEngineNumberFromChassis() {
      if (!this.canFetchEngineNumber()) return;

      if (!this.sourceStoreId) {
        this.setError('chassis', 'Vui lòng chọn cửa hàng để kiểm tra số khung');
        return;
      }

      this.isLoading = true;

      try {
        const engineNumber =
          await productService.getEngineNumberByChassisNumber(
            this.formData.chassisNumber,
            this.sourceStoreId,
            this.isExportMode,
          );

        this.handleEngineNumberResponse(engineNumber);
      } catch (error) {
        this.handleEngineNumberError(error);
      } finally {
        this.isLoading = false;
        this.validateEngineNumber();
      }
    },

    canFetchEngineNumber() {
      return (
        this.shouldFetchEngineNumber &&
        this.formData.chassisNumber &&
        this.isChassisLengthValid
      );
    },

    handleEngineNumberResponse(engineNumber) {
      if (engineNumber) {
        this.validEngineNumberFromChassis = engineNumber;
        this.includeEngineNumber = true;
        this.formData.engineNumber = engineNumber;
        this.autoDetectedEngine = true;
        this.clearAllErrors();
      } else {
        this.autoDetectedEngine = false;
        this.setError('engine', 'Không tìm thấy số máy');
      }
    },

    handleEngineNumberError(error) {
      console.error('Error fetching engine number:', error);
      this.autoDetectedEngine = false;
      this.setError('chassis', 'Có lỗi xảy ra khi kiểm tra số khung');
    },

    // Validation methods
    validateEngineNumber() {
      if (!this.shouldFetchEngineNumber || !this.includeEngineNumber) return;

      if (
        this.validEngineNumberFromChassis &&
        this.validEngineNumberFromChassis !== this.formData.engineNumber
      ) {
        this.setError('engine', 'Số máy không khớp với số khung');
      }
    },

    validateForm() {
      this.clearAllErrors();
      let isValid = true;

      if (this.isChassisEngineProduct) {
        if (!this.formData.chassisNumber.trim()) {
          this.setError('chassis', 'Số khung không được để trống');
          isValid = false;
        }

        if (this.includeEngineNumber && !this.formData.engineNumber.trim()) {
          this.setError('engine', 'Số máy không được để trống');
          isValid = false;
        }
      } else {
        if (!this.formData.serialNumber.trim()) {
          this.setError('serial', 'Số seri không được để trống');
          isValid = false;
        }
      }

      return isValid;
    },

    getValidationState(field) {
      return this.errors[field] ? false : null;
    },

    // Error handling methods
    setError(field, message) {
      this.$set(this.errors, field, message);
    },

    clearError(field) {
      this.$set(this.errors, field, '');
    },

    clearErrors(fields) {
      fields.forEach((field) => this.clearError(field));
    },

    clearAllErrors() {
      VALIDATION_FIELDS.forEach((field) => this.clearError(field));
    },

    // Cleanup methods
    cancelDebouncedFunctions() {
      if (this.debouncedFetchEngineNumber) {
        this.debouncedFetchEngineNumber.cancel();
      }
    },
  },
};
</script>

<style scoped>
.text-muted {
  font-size: 0.875rem;
}

.py-2 {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

/* Custom styling for the toggle switch */
#engine-toggle {
  margin-bottom: 0.5rem;
}

#engine-toggle + small {
  display: block;
  margin-top: 0.25rem;
}
</style>
